import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface ActionState {
  startNewChat: boolean;
  pickProjectsModal: boolean;
  chatHistoryPane: boolean;
  chatHistoryDrawer: boolean;
  confirmVisible: boolean;
  addAdminModal: boolean;
  viewAdminModal: boolean;
  kycModal: boolean;
  temporaryChat: boolean;
  selectAnEpic?: boolean;
  deleteIntegration: boolean;
  updateAPIKeyModal: boolean;
  openAIActiveComponent: string;
  jiraActiveComponent: string;
  integrationSuccessModal: boolean;
  integrationFailedModal: boolean;
  collapsed: boolean;
  chatCollapsed: boolean;
  subscriptionWarning: boolean;
  slackSuccess: boolean;
  showSidebarBanner: boolean;
  promptInputWarning: boolean;
  promptInputWarningMessage: string;
  integrationModal: boolean;
  logoutModal: boolean;
  taskModal: boolean;
  createTaskModal: boolean;
  taskOverviewDrawer: boolean;
  taskDrawer: boolean;
  createNewTeamModal: boolean;
  addMemberToTeamModal: boolean;
  chooseProjectsModal: boolean;

  toggleStartNewChat: (isOpen: boolean) => void;
  togglePickProjectsModal: (isOpen: boolean) => void;
  toggleChatHistoryPane: (isOpen: boolean) => void;
  toggleChatHistoryDrawer: (isOpen: boolean) => void;
  toggleConfirmVisible: (isOpen: boolean) => void;
  toggleAddAdminModal: (isOpen: boolean) => void;
  toggleViewAdminModal: (isOpen: boolean) => void;
  toggleTemporaryChat: (isOpen: boolean) => void;
  toggleSelectAnEpic: (isOpen: boolean) => void;
  toggleDeleteIntegration: (isOpen: boolean) => void;
  toggleUpdateAPIKeyModal: (isOpen: boolean) => void;
  toggleKycModal: (isOpen: boolean) => void;
  toggleIntegrationSuccessModal: (isOpen: boolean) => void;
  toggleIntegrationFailedModal: (isOpen: boolean) => void;
  setCollapsed: (isCollapsed: boolean) => void;
  setChatCollapsed: (isCollapsed: boolean) => void;
  setStartNewChat: (startNewChat: boolean) => void;
  setPickProjectsModal: (pickProjectsModal: boolean) => void;
  toggleSubscriptionWarning: (isOpen: boolean) => void;
  toggleSlackSuccessModal: (isOpen: boolean) => void;
  toggleShowSidebarBanner: (isOpen: boolean) => void;
  togglePromptInputWarning: (isOpen: boolean) => void;
  setPromptInputWarningMessage: (message: string) => void;
  toggleIntegrationModal: (isOpen: boolean) => void;
  toggleLogoutModal: (isOpen: boolean) => void;
  toggleTaskModal: (isOpen: boolean) => void;
  toggleCreateTaskModal: (isOpen: boolean) => void;
  toggleCreateNewTeamModal: (isOpen: boolean) => void;
  toggleAddMemberToTeamModal: (isOpen: boolean) => void;
  toggleTaskOverviewDrawer: (isOpen: boolean) => void;
  toggleTaskDrawer: (isOpen: boolean) => void;
  toggleConfirmVisibleState: () => void;
  toggleAddAdminModalState: () => void;
  toggleViewAdminModalState: () => void;
  toggleTemporaryChatState: () => void;
  toggleDeleteIntegrationState: () => void;
  toggleUpdateAPIKeyModalState: () => void;
  toggleKycModalState: () => void;
  toggleIntegrationSuccessModalState: () => void;
  toggleIntegrationFailedModalState: () => void;
  toggleSidebarCollapsedState: () => void;
  setOpenAIActiveComponent: (component: string) => void;
  setJiraActiveComponent: (component: string) => void;
  setChooseProjectsModal: (isOpen: boolean) => void;
}

const SIDEBAR_COLLAPSED_SESSION_KEY = 'sidebarCollapsed';

const getInitialCollapsedState = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return sessionStorage.getItem(SIDEBAR_COLLAPSED_SESSION_KEY) === 'true';
};

const persistCollapsedState = (isCollapsed: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(SIDEBAR_COLLAPSED_SESSION_KEY, String(isCollapsed));
};

const initialState = {
  startNewChat: false,
  pickProjectsModal: false,
  chatHistoryPane: true,
  chatHistoryDrawer: false,
  confirmVisible: false,
  addAdminModal: false,
  viewAdminModal: false,
  kycModal: false,
  temporaryChat: false,
  deleteIntegration: false,
  updateAPIKeyModal: false,
  openAIActiveComponent: '',
  jiraActiveComponent: '',
  integrationSuccessModal: false,
  integrationFailedModal: false,
  collapsed: getInitialCollapsedState(),
  chatCollapsed: true,
  subscriptionWarning: false,
  slackSuccess: false,
  showSidebarBanner: false,
  promptInputWarning: false,
  promptInputWarningMessage: '',
  integrationModal: false,
  logoutModal: false,
  taskModal: false,
  createTaskModal: false,
  taskOverviewDrawer: false,
  taskDrawer: false,
  createNewTeamModal: false,
  addMemberToTeamModal: false,
  chooseProjectsModal: false,
};

const useActionStore = create<ActionState>()(
  devtools(
    (set) => ({
      ...initialState,
      toggleStartNewChat: (isOpen) => set({ startNewChat: isOpen }),
      togglePickProjectsModal: (isOpen) => set({ pickProjectsModal: isOpen }),
      toggleChatHistoryPane: (isOpen) => set({ chatHistoryPane: isOpen }),
      toggleChatHistoryDrawer: (isOpen) => set({ chatHistoryDrawer: isOpen }),
      toggleConfirmVisible: (isOpen) => set({ confirmVisible: isOpen }),
      toggleAddAdminModal: (isOpen) => set({ addAdminModal: isOpen }),
      toggleViewAdminModal: (isOpen) => set({ viewAdminModal: isOpen }),

      toggleTemporaryChat: (isOpen) => set({ temporaryChat: isOpen }),
      toggleSelectAnEpic: (isOpen) => set({ selectAnEpic: isOpen }),
      toggleDeleteIntegration: (isOpen) => set({ deleteIntegration: isOpen }),
      toggleUpdateAPIKeyModal: (isOpen) => set({ updateAPIKeyModal: isOpen }),

      toggleKycModal: (isOpen) => set({ kycModal: isOpen }),
      toggleIntegrationSuccessModal: (isOpen) =>
        set({ integrationSuccessModal: isOpen }),
      toggleIntegrationFailedModal: (isOpen) =>
        set({ integrationFailedModal: isOpen }),

      setCollapsed: (isCollapsed) => {
        persistCollapsedState(isCollapsed);
        set({ collapsed: isCollapsed });
      },
      setChatCollapsed: (isCollapsed) => set({ chatCollapsed: isCollapsed }),
      setStartNewChat: (startNewChat) => set({ startNewChat: startNewChat }),
      setPickProjectsModal: (pickProjectsModal) =>
        set({ pickProjectsModal: pickProjectsModal }),

      toggleSubscriptionWarning: (isOpen) =>
        set({ subscriptionWarning: isOpen }),
      toggleSlackSuccessModal: (isOpen) => set({ slackSuccess: isOpen }),
      toggleShowSidebarBanner: (isOpen) => set({ showSidebarBanner: isOpen }),

      togglePromptInputWarning: (isOpen) => set({ promptInputWarning: isOpen }),

      setPromptInputWarningMessage: (message) =>
        set({ promptInputWarningMessage: message }),

      toggleIntegrationModal: (isOpen) => set({ integrationModal: isOpen }),
      toggleLogoutModal: (isOpen) => set({ logoutModal: isOpen }),
      toggleTaskModal: (isOpen) => set({ taskModal: isOpen }),
      toggleCreateTaskModal: (isOpen) => set({ createTaskModal: isOpen }),
      toggleCreateNewTeamModal: (isOpen) => set({ createNewTeamModal: isOpen }),
      toggleAddMemberToTeamModal: (isOpen) =>
        set({ addMemberToTeamModal: isOpen }),

      toggleTaskOverviewDrawer: (isOpen) => set({ taskOverviewDrawer: isOpen }),

      // ---------------------------------------------------

      toggleConfirmVisibleState: () =>
        set((state) => ({ confirmVisible: !state.confirmVisible })),
      toggleAddAdminModalState: () =>
        set((state) => ({ addAdminModal: !state.addAdminModal })),
      toggleViewAdminModalState: () =>
        set((state) => ({ viewAdminModal: !state.viewAdminModal })),
      toggleTemporaryChatState: () =>
        set((state) => ({ temporaryChat: !state.temporaryChat })),

      toggleDeleteIntegrationState: () =>
        set((state) => ({ deleteIntegration: !state.deleteIntegration })),
      toggleUpdateAPIKeyModalState: () =>
        set((state) => ({ updateAPIKeyModal: !state.updateAPIKeyModal })),
      toggleKycModalState: () =>
        set((state) => ({ kycModal: !state.kycModal })),

      toggleIntegrationSuccessModalState: () =>
        set((state) => ({
          integrationSuccessModal: !state.integrationSuccessModal,
        })),
      toggleIntegrationFailedModalState: () =>
        set((state) => ({
          integrationFailedModal: !state.integrationFailedModal,
        })),

      toggleSidebarCollapsedState: () =>
        set((state) => {
          const nextCollapsedState = !state.collapsed;
          persistCollapsedState(nextCollapsedState);
          return { collapsed: nextCollapsedState };
        }),

      setOpenAIActiveComponent: (component) =>
        set({ openAIActiveComponent: component }),

      setJiraActiveComponent: (component) =>
        set({ jiraActiveComponent: component }),

      setChooseProjectsModal: (isOpen) => set({ chooseProjectsModal: isOpen }),

      toggleTaskDrawer: (isOpen) => set({ taskDrawer: isOpen }),
    }),
    {
      name: 'ActionStore',
    }
  )
);

export default useActionStore;
